import React from 'react';
import Barcode from './Barcode';
import ScaleWeighingSummary from './ScaleWeighingSummary';
import { FiInfo } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import { normalizeQuantity } from '../utils/quantity';
import { isScaleWeightMetricUnit } from '../utils/scalePresentation';

const truncateDescription = (value, maxLength = 80) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}...`;
};

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const formatQuantity = (value) => {
  const quantity = normalizeQuantity(value);
  const hasDecimals = Math.abs(quantity - Math.round(quantity)) > 0.0001;
  return quantity.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const resolveVarietyDescription = (variety) =>
  String(variety?.description || '').trim();

const isSameScaleSelection = (selection, varietyId, priceIndex, price) => {
  if (!selection) return false;
  if (Number(selection.varietyId) !== Number(varietyId)) return false;
  if (Number.isInteger(selection.priceIndex) && Number(selection.priceIndex) === Number(priceIndex)) {
    return true;
  }
  return selection.varietyPriceId != null && Number(selection.varietyPriceId) === Number(price?.id);
};

const VarietyDetailModal = ({
  variety,
  onClose,
  handleClick,
  allowSoldOutSelection = false,
  priceFilter,
  resolveDisplayPrice,
  quantities = {},
  discounts = {},
  activeScaleSelection = null,
  scaleWeightKg = null,
  onPriceIncrement,
  onPriceDecrement,
  onPriceKeypad,
  onBackToVarieties,
  onConfirmClose,
  showPrices = true,
  showBarcode = showPrices,
  showScaleSummary = true,
  overlayZClassName = 'z-50',
}) => {
  const [selectedPriceKey, setSelectedPriceKey] = React.useState(null);
  React.useEffect(() => {
    setSelectedPriceKey(null);
  }, [variety?.id]);

  const getPriceSelectionKey = (price, priceIndex) => (
    `${variety?.id ?? 'variety'}:${price?.id ?? priceIndex ?? 'price'}:${priceIndex ?? ''}`
  );
  const hasCardActions = (
    typeof onPriceIncrement === 'function'
    || typeof onPriceDecrement === 'function'
    || typeof onPriceKeypad === 'function'
  );
  const actClick = (price, priceIndex, isDisabled) => {
    if (isDisabled) return;
    setSelectedPriceKey(getPriceSelectionKey(price, priceIndex));
    if (handleClick) {
      handleClick(price);
    }
  };
  const sellablePrices = (variety.VarietyPrices || [])
    .map((price, priceIndex) => ({ price, priceIndex }))
    .filter(({ price }) => (
      showPrices ? price.price !== null && price.price !== undefined : Boolean(price)
    ));
  const filteredPrices = sellablePrices.filter(({ price }) =>
    priceFilter ? priceFilter(price) : true
  );
  const pricedEntries = filteredPrices.map(({ price, priceIndex }) => {
    const basePrice = Math.max(0, Number(price.price || 0));
    const matrixDiscount = Number(discounts?.[variety?.id]?.[priceIndex] || 0);
    const priceDiscount = Number(price.discount || 0);
    const unitDiscount = Math.min(
      Math.max(matrixDiscount > 0 ? matrixDiscount : priceDiscount, 0),
      basePrice
    );
    const baseDisplay = typeof resolveDisplayPrice === 'function'
      ? resolveDisplayPrice(variety, price, 0)
      : null;
    const display = typeof resolveDisplayPrice === 'function'
      ? resolveDisplayPrice(variety, price, unitDiscount)
      : null;
    const shownBasePrice = Number.isFinite(Number(baseDisplay?.price))
      ? Number(baseDisplay.price)
      : basePrice;
    const shownPrice = Number.isFinite(Number(display?.price))
      ? Number(display.price)
      : Number.isFinite(Number(price.finalPrice)) && unitDiscount > 0
        ? Number(price.finalPrice)
        : Math.max(basePrice - unitDiscount, 0);
    return {
      price,
      priceIndex,
      shownPrice,
      shownBasePrice,
      unitDiscount,
      discountSource: price.discountSource || (unitDiscount > 0 ? 'client_category' : 'none'),
      discountType: price.discountType || null,
      discountValue: price.discountValue || 0,
      clientPricingCategoryName: price.clientPricingCategoryName || '',
      taxFreeApplied: Boolean(baseDisplay?.taxFreeApplied || display?.taxFreeApplied),
      quantity: normalizeQuantity(quantities?.[variety?.id]?.[priceIndex] || 0),
    };
  });
  const sortedPricedEntries = showPrices
    ? pricedEntries.slice().sort((entryA, entryB) => entryB.shownPrice - entryA.shownPrice)
    : pricedEntries;
  const varietyDescription = resolveVarietyDescription(variety);
  const isVarietySoldOut = filteredPrices.length > 0 && filteredPrices.every(({ price }) => price.isSoldOut);
  const hasScalePrices = showScaleSummary && pricedEntries.some(({ price }) => (
    price?.useScaleWeight && isScaleWeightMetricUnit(price?.metricUnit)
  ));
  const activeScaleEntry = pricedEntries.find(({ price, priceIndex }) => (
    price?.useScaleWeight
    && isScaleWeightMetricUnit(price?.metricUnit)
    && isSameScaleSelection(activeScaleSelection, variety?.id, priceIndex, price)
  ));
  const scaleSummaryWeightKg = Number(scaleWeightKg);
  const scaleSummaryUnitPrice = activeScaleEntry ? activeScaleEntry.shownPrice : null;
  const selectedPriceEntry = pricedEntries.find(({ price, priceIndex }) => (
    selectedPriceKey === getPriceSelectionKey(price, priceIndex)
  ));
  const selectedPriceIsScale = showScaleSummary && Boolean(
    selectedPriceEntry?.price?.useScaleWeight
    && isScaleWeightMetricUnit(selectedPriceEntry?.price?.metricUnit)
  );
  const summaryMode = showPrices && selectedPriceEntry && !selectedPriceIsScale
    ? 'quantity'
    : showPrices && hasScalePrices
      ? 'scale'
      : null;
  const summaryThemeClass = summaryMode === 'quantity'
    ? 'border-blue-100 bg-blue-50/80 text-blue-950'
    : 'border-emerald-100 bg-emerald-50/80 text-emerald-950';
  const summaryTitleClass = summaryMode === 'quantity'
    ? 'text-blue-700'
    : 'text-emerald-700';

  const renderPriceCard = (entry) => {
    const {
      price,
      priceIndex,
      shownPrice,
      shownBasePrice,
      unitDiscount,
      discountType,
      discountValue,
      clientPricingCategoryName,
      taxFreeApplied,
      quantity,
    } = entry;
    const priceKey = getPriceSelectionKey(price, priceIndex);
    const isDisabled = price.isSoldOut && !allowSoldOutSelection;
    const isScalePrice = Boolean(price?.useScaleWeight && isScaleWeightMetricUnit(price?.metricUnit));
    const isSelectedScalePrice = isScalePrice && isSameScaleSelection(activeScaleSelection, variety?.id, priceIndex, price);
    const isSelectedPrice = selectedPriceKey === priceKey;
    const hasCartQuantity = normalizeQuantity(quantity) > 0;
    const quantityLabel = formatQuantity(quantity);
    const subtotal = Math.floor(shownPrice * quantity);
    const description = truncateDescription(price.description || varietyDescription) || '';
    const discountAmountLabel = formatMoney(Math.max(shownBasePrice - shownPrice, 0));
    const hasUnitDiscount = showPrices && unitDiscount > 0 && shownBasePrice > shownPrice;
    const discountValueLabel = discountType === 'percent' && Number(discountValue) > 0
      ? ` (${Number(discountValue).toLocaleString('de-DE')}%)`
      : '';
    const handleCardAction = (callback) => {
      if (isDisabled) return;
      setSelectedPriceKey(priceKey);
      callback?.(price, priceIndex);
    };

    return (
      <article
        key={price?.id || priceIndex}
        aria-disabled={isDisabled}
        className={`relative overflow-hidden rounded-xl border bg-white shadow-sm transition ${
          isDisabled
            ? 'cursor-not-allowed border-gray-200 opacity-70'
            : isSelectedScalePrice
              ? 'border-emerald-300 shadow-md ring-2 ring-emerald-100'
              : isSelectedPrice
                ? 'border-blue-300 shadow-md ring-2 ring-blue-100'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
        }`}
      >
        {hasCardActions ? (
          <div className="absolute inset-0 grid grid-cols-2">
            <button
              type="button"
              onClick={() => handleCardAction(onPriceDecrement)}
              disabled={isDisabled}
              className={`min-h-full border-r border-transparent transition ${
                isDisabled ? 'cursor-not-allowed bg-gray-50/40' : 'bg-red-50/0 hover:bg-red-50/70'
              }`}
              title="Click a la izquierda para restar"
              aria-label={`Restar ${price.metricUnit || 'precio'}`}
            />
            <button
              type="button"
              onClick={() => handleCardAction(onPriceIncrement)}
              disabled={isDisabled}
              className={`min-h-full transition ${
                isDisabled ? 'cursor-not-allowed bg-gray-50/40' : 'bg-emerald-50/0 hover:bg-emerald-50/70'
              }`}
              title="Click a la derecha para sumar"
              aria-label={`Sumar ${price.metricUnit || 'precio'}`}
            />
          </div>
        ) : (
          <button
            type="button"
            aria-label={`Seleccionar ${price.metricUnit || 'precio'}`}
            onClick={() => actClick(price, priceIndex, isDisabled)}
            disabled={isDisabled}
            className="absolute inset-0"
          />
        )}

        {price.isSoldOut && (
          <span className="absolute right-2 top-2 z-20 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
            Agotado
          </span>
        )}
        {isSelectedScalePrice && (
          <span className="absolute left-1/2 top-2 z-20 -translate-x-1/2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
            Seleccionado
          </span>
        )}

        <div className="relative z-10 pointer-events-none p-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="relative shrink-0">
              <img
                src={price.priceImage || variety.varietyImage || 'https://placehold.co/100'}
                alt={variety.varietyName}
                className="h-14 w-14 rounded-lg object-cover shadow"
              />
              {hasCartQuantity && (
                <span
                  className="absolute -right-2 -top-2 flex min-h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1.5 text-xs font-black leading-none text-white shadow"
                  title={`Cantidad: ${quantityLabel}`}
                  aria-label={`Cantidad: ${quantityLabel}`}
                >
                  {quantityLabel}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-700">{price.metricUnit}</p>
              {showPrices ? (
                <div>
                  {hasUnitDiscount && (
                    <span className="block text-xs font-semibold text-gray-400 line-through">
                      {formatMoney(shownBasePrice)}
                    </span>
                  )}
                  <span className="inline-flex items-center text-lg font-bold text-gray-900">
                    {formatMoney(shownPrice)}
                    {taxFreeApplied && (
                      <span
                        className="ml-1 inline-flex text-blue-700"
                        title="Precio ajustado sin impuestos excluidos."
                        aria-label="Precio ajustado sin impuestos excluidos."
                      >
                        <FiInfo size={13} />
                      </span>
                    )}
                  </span>
                </div>
              ) : (
                <span className="inline-flex items-center text-sm font-semibold text-blue-700">
                  Formato disponible
                </span>
              )}
            </div>
            {hasCardActions && (
              <button
                type="button"
                onClick={() => handleCardAction(onPriceKeypad)}
                disabled={isDisabled}
                className={`pointer-events-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${
                  isDisabled
                    ? 'border-gray-200 bg-gray-100 text-gray-400'
                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                title="Cantidad exacta"
                aria-label="Cantidad exacta"
              >
                <FaCalculator size={16} />
              </button>
            )}
          </div>

          <p className="mt-3 min-h-[2.5rem] break-all text-sm leading-snug text-gray-600">
            {description || '\u200e'}
          </p>

          {hasUnitDiscount && (
            <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
              Descuento{clientPricingCategoryName ? ` ${clientPricingCategoryName}` : ''}: -{discountAmountLabel}{discountValueLabel}
            </div>
          )}

          {showPrices && (
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              <div className="text-sm font-bold text-gray-900">
                Subtotal: {formatMoney(subtotal)}
              </div>
            </div>
          )}

          {showBarcode && (
            price.barcode ? (
              <Barcode value={price.barcode} className="mt-3" />
            ) : (
              <p className="mt-3 text-center text-xs text-gray-500">
                Sin codigo de barras
              </p>
            )
          )}

        </div>
      </article>
    );
  };

  return (
    <div
      className={`fixed inset-0 ${overlayZClassName} flex items-start justify-center overflow-y-auto bg-black/50 px-3 py-4 sm:px-4 sm:py-6`}
      style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl"
        style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-gray-100 lg:hidden">
              <img
                src={variety.varietyImage || 'https://placehold.co/400'}
                alt={variety.varietyName}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-gray-500">Variedad</p>
              <h2 className="break-words text-lg font-semibold text-gray-900">{variety.varietyName}</h2>
              {varietyDescription && (
                <p className="mt-1 text-sm leading-snug text-gray-600 lg:hidden">
                  {truncateDescription(varietyDescription, 120)}
                </p>
              )}
              {isVarietySoldOut && (
                <span className="mt-1 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                  AGOTADO
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900"
          >
            X
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto p-4 sm:gap-6 sm:p-6 lg:grid-cols-[240px_1fr]">
          <div className="hidden space-y-3 sm:space-y-4 lg:block">
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
              <div className="text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Imagen</div>
              <div className="relative mx-auto w-20 h-20 sm:w-28 sm:h-28 lg:w-full lg:aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                <img
                  src={variety.varietyImage || 'https://placehold.co/400'}
                  alt={variety.varietyName}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
            {variety.varietyName && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="text-xs font-semibold text-gray-600 uppercase">Nombre</div>
                <p className="mt-2 text-sm text-gray-700">{variety.varietyName}</p>
              </div>
            )}
            {varietyDescription && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="text-xs font-semibold text-gray-600 uppercase">Descripcion</div>
                <p
                  className="mt-2 text-sm text-gray-700 break-words"
                  title={varietyDescription}
                >
                  {truncateDescription(varietyDescription, 140)}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {showPrices ? 'Precios disponibles' : 'Formatos disponibles'}
              </h3>
            </div>
            {summaryMode && (
              <div className={`rounded-2xl border p-3 shadow-sm ${summaryThemeClass}`}>
                <div className={`text-xs font-semibold uppercase tracking-wide ${summaryTitleClass}`}>
                  {summaryMode === 'quantity' ? 'Precio seleccionado' : 'Lectura de balanza'}
                </div>
                <ScaleWeighingSummary
                  weightKg={summaryMode === 'quantity'
                    ? selectedPriceEntry?.quantity ?? 0
                    : Number.isFinite(scaleSummaryWeightKg) && scaleSummaryWeightKg >= 0 ? scaleSummaryWeightKg : null}
                  unitPrice={summaryMode === 'quantity' ? selectedPriceEntry?.shownPrice : scaleSummaryUnitPrice}
                  amountTitle={summaryMode === 'quantity' ? 'Cantidad' : 'Peso'}
                  amountLabel={summaryMode === 'quantity'
                    ? formatQuantity(selectedPriceEntry?.quantity ?? 0)
                    : undefined}
                  allowZeroAmount
                  className="mt-3 gap-2"
                  cardClassName="rounded-xl bg-white/75 px-3 py-2"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sortedPricedEntries.length > 0 ? (
                sortedPricedEntries.map(renderPriceCard)
              ) : (
                <p className="col-span-full text-sm text-gray-500 text-center">
                  {showPrices ? 'Sin precio disponible' : 'Sin formato disponible'}
                </p>
              )}
            </div>
          </div>
        </div>
        <div
          className={`flex shrink-0 flex-wrap border-t border-gray-100 bg-white px-4 py-3 sm:px-6 ${
          onBackToVarieties ? 'justify-between gap-3' : 'justify-end'
        }`}
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          {onBackToVarieties && (
            <button
              type="button"
              onClick={onBackToVarieties}
              className="rounded-xl border border-gray-300 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              Volver a variedades
            </button>
          )}
          <button
            type="button"
            onClick={onConfirmClose || onClose}
            className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default VarietyDetailModal;
